import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseChangelog, type Version } from "../lib/changelogParser";
import { VersionEntry } from "../components/changelog/VersionEntry";
import { refreshToken } from "../lib/api/cloud";

function VersionNav({ versions }: { versions: Version[] }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const visibleVersions = versions.slice(0, 10);

  return (
    <div className="lg:w-64 flex-shrink-0">
      <div className="lg:sticky lg:top-20">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden w-full flex items-center justify-between px-4 py-2 bg-dark-card border border-dark-border rounded-lg text-warm-100"
        >
          <span className="font-medium">{t("changelog.jumpToVersion")}</span>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <nav className={`${isOpen ? "block" : "hidden"} lg:block mt-4 lg:mt-0`}>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-warm-500 uppercase tracking-wider mb-3">
              {t("changelog.versions")}
            </h3>
            <ul className="space-y-1">
              {visibleVersions.map((version) => (
                <li key={version.version}>
                  <a
                    href={`#v${version.version.replace(/\./g, "-")}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-warm-200 hover:text-warm-50 hover:bg-dark-hover transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="font-mono">{version.version}</span>
                    {version.isLatest && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
                        {t("changelog.latest")}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
            {versions.length > 10 && (
              <p className="mt-3 text-xs text-warm-500">
                {t("changelog.moreVersions", { count: versions.length - 10 })}
              </p>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}

export function Changelog() {
  const { t } = useTranslation();
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadChangelog() {
      const token = localStorage.getItem("cloud_access_token");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      let res = await fetch("/api/changelog", { headers });

      // On 401, attempt token refresh and retry once
      if (res.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          res = await fetch("/api/changelog", {
            headers: { Authorization: `Bearer ${refreshed.accessToken}` },
          });
        }
      }

      // Still 401 after refresh attempt — clear token to trigger login redirect
      if (res.status === 401) {
        localStorage.removeItem("cloud_access_token");
        return;
      }

      if (!res.ok) {
        throw new Error(`Failed to load changelog (${res.status})`);
      }

      return res.text();
    }

    loadChangelog()
      .then((text) => {
        if (text) setContent(text);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  const versions = useMemo(() => (content ? parseChangelog(content) : []), [content]);

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t("changelog.error", { error })}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t("changelog.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-dark-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-warm-50 mb-3">{t("changelog.title")}</h1>
          <p className="text-warm-400 text-lg">
            {t("changelog.subtitle")}
          </p>
        </header>

        <div className="lg:flex lg:gap-8">
          <div className="flex-1 min-w-0">
            <div className="space-y-8">
              {versions.map((version) => (
                <VersionEntry key={version.version} version={version} />
              ))}
            </div>
          </div>

          <VersionNav versions={versions} />
        </div>
      </div>
    </div>
  );
}
